import { Eye } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { C } from "./homeTheme";
import { FORT_AGENT_STATUS, MyFortAgentItem } from "@/api/types";
import {
  formatEtherWithDecimals,
  formatThousands,
  formatUsdPrefix,
} from "@/lib/utils";
import { getAgentTierKey, getTokenName } from "@/config/common";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  AGENT_IMAGE_URL_INACTIVE,
  calcAifProgressPct,
} from "@/components/home/MyAgentsSection/constants";
import { CCC_AIF_ADDRESS, CCC_FUSD_ADDRESS, USDT_ADDRESS } from "@/config/env";

const PANEL_STYLE = {
  background: "rgba(255,255,255,0.02)",
  border: `1px solid ${C.border}`,
} as const;

export function MyAgentInactiveDetailDialog({
  agent,
  open,
  onClose,
}: {
  agent: MyFortAgentItem | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  if (!agent || agent.status === FORT_AGENT_STATUS.ACTIVATED) return null;

  const mustPayFusdBeforeActivate =
    agent.paymentMode === "mixed" &&
    !agent.fusdPurchaseDeducted &&
    BigInt(agent.purchasePayFusdWei || "0") > 0n;

  const hasFusdInPurchase =
    agent.paymentMode === "mixed" &&
    BigInt(agent.purchasePayFusdWei || "0") > 0n;

  const tierLabel = t(getAgentTierKey(agent.name));
  const tagColor = C.amber;
  const purchaseDateStr = dayjs(agent.createdAt).format("YYYY-MM-DD");
  const aifProgressPct = calcAifProgressPct(agent);
  const tokenNameAif = getTokenName(CCC_AIF_ADDRESS);
  const tokenNameFusd = getTokenName(CCC_FUSD_ADDRESS);
  const tokenNameUsdt = getTokenName(USDT_ADDRESS);

  const purchasePriceDisplay = formatUsdPrefix(
    formatThousands(formatEtherWithDecimals(agent.purchasePayTotalPrice, 0), 0)
  );
  const staticCapDisplay = formatThousands(
    formatEtherWithDecimals(agent.staticCapUsdt, 0),
    0
  );
  const aifTotalDisplay = formatUsdPrefix(
    formatThousands(formatEtherWithDecimals(agent.aifValueUsdt, 0), 0)
  );
  const aifClaimedDisplay = formatUsdPrefix(
    formatThousands(formatEtherWithDecimals(agent.aifPaidValueUsdt, 0), 0)
  );
  const dailyAifDisplay = formatUsdPrefix(
    formatThousands(
      formatEtherWithDecimals(
        agent.aifDistributeDays > 0
          ? BigInt(agent.aifValueUsdt || "0") / BigInt(agent.aifDistributeDays)
          : 0n,
        0
      ),
      0
    )
  );
  const fusdAmountDisplay = formatThousands(
    formatEtherWithDecimals(agent.purchasePayFusdWei, 0),
    0
  );

  const benefits: { label: string; value: string; color: string }[] = [
    {
      label: t("homePage.benefit.custodyBoost"),
      value: `${agent.aiManageAddSpeedBp / 100}%`,
      color: C.primary,
    },
    {
      label: t("homePage.benefit.releaseMultiple"),
      value: `${agent.exitMultiple / 100}x`,
      color: C.green,
    },
    {
      label: t("homePage.benefit.aifToken", { tokenNameAif }),
      value: aifTotalDisplay,
      color: C.amber,
    },
  ];
  if (agent.giftGiveLevel > 0) {
    benefits.push({
      label: t("homePage.benefit.teamLevel"),
      value: `C${agent.giftGiveLevel}`,
      color: C.cyan,
    });
  }

  const purchaseRows: { label: string; value: string; color?: string }[] = [
    {
      label: t("homePage.myAgentCard.purchasePrice"),
      value: purchasePriceDisplay,
    },
    {
      label: t("homePage.myAgentInactiveDetailDialog.exitCap"),
      value: formatUsdPrefix(staticCapDisplay),
      color: C.green,
    },
    {
      label: t("homePage.myAgentActiveDetailDialog.purchaseDate"),
      value: purchaseDateStr,
    },
  ];

  if (hasFusdInPurchase) {
    purchaseRows.push({
      label: tokenNameFusd,
      value: mustPayFusdBeforeActivate
        ? t("homePage.myAgentInactiveDetailDialog.pendingFusdNotice", {
            amount: fusdAmountDisplay,
            tokenName: tokenNameFusd,
            tokenNameUsdt,
          })
        : t("homePage.myAgentInactiveDetailDialog.paidFusdNotice", {
            amount: fusdAmountDisplay,
            tokenName: tokenNameFusd,
            tokenNameUsdt,
          }),
      color: mustPayFusdBeforeActivate ? C.red : C.green,
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) onClose();
      }}
    >
      <DialogContent
        className="max-w-md border-0 p-0 overflow-hidden mx-auto"
        style={{ background: C.card, borderRadius: 16, color: C.text }}
      >
        <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle
              className="text-sm font-bold flex items-center gap-2"
              style={{ color: C.text }}
            >
              <Eye size={14} style={{ color: C.cyan }} />
              {t("homePage.myAgentActiveDetailDialog.title")}
            </DialogTitle>
          </DialogHeader>

          {/* 头部信息 */}
          <div
            className="flex items-center gap-3 rounded-xl p-3"
            style={PANEL_STYLE}
          >
            <div
              className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0"
              style={{ background: "rgba(255,255,255,0.03)" }}
            >
              <img
                src={AGENT_IMAGE_URL_INACTIVE}
                alt=""
                className="w-full h-full object-contain"
                style={{ opacity: 0.55 }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold" style={{ color: C.text }}>
                  {agent.name}
                </span>
                <span
                  className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                  style={{
                    background: `${tagColor}15`,
                    color: tagColor,
                    border: `1px solid ${tagColor}20`,
                  }}
                >
                  {tierLabel}
                </span>
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: C.dim }}>
                {t("homePage.myAgentInactiveDetailDialog.subtitle", {
                  date: purchaseDateStr,
                })}
              </div>
            </div>
            <span
              className="text-[10px] px-2 py-1 rounded-full font-bold shrink-0"
              style={{
                background: "rgba(100,116,139,0.08)",
                color: C.dim,
              }}
            >
              {t("homePage.myAgentCard.statusPending")}
            </span>
          </div>

          {/* 四项权益 */}
          <div
            className={`grid gap-1.5 ${
              benefits.length === 4 ? "grid-cols-4" : "grid-cols-3"
            }`}
          >
            {benefits.map(b => (
              <div
                key={b.label}
                className="text-center py-2.5 rounded-lg"
                style={{
                  background: `${b.color}08`,
                  border: `1px solid ${b.color}12`,
                }}
              >
                <div className="text-[9px] mb-0.5" style={{ color: C.dim }}>
                  {b.label}
                </div>
                <div
                  className="text-sm font-bold mono-num truncate px-0.5"
                  style={{ color: b.color }}
                >
                  {b.value}
                </div>
              </div>
            ))}
          </div>

          {/* AIF 代币发放 */}
          <div className="rounded-xl p-3.5 space-y-2.5" style={PANEL_STYLE}>
            <div className="flex justify-between items-center">
              <span
                className="flex items-center gap-1.5 text-[11px] font-medium"
                style={{ color: C.dim }}
              >
                <span
                  className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold"
                  style={{ background: `${C.amber}20`, color: C.amber }}
                >
                  {tokenNameAif.slice(0, 1)}
                </span>
                {t("homePage.myAgentInactiveDetailDialog.aifSectionTitle", {
                  tokenNameAif,
                })}
              </span>
              <span
                className="text-xs mono-num font-bold"
                style={{ color: C.amber }}
              >
                {t("homePage.myAgentActiveDetailDialog.aifDaysProgress", {
                  paid: agent.aifPaidDays,
                  total: agent.aifDistributeDays,
                })}
              </span>
            </div>
            <div
              className="h-2 rounded-full overflow-hidden"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, aifProgressPct)}%`,
                  background: `linear-gradient(90deg, ${C.amber}, ${C.amber})`,
                }}
              />
            </div>
            <div className="flex justify-between text-[10px]">
              <span style={{ color: C.dim }}>
                {t("homePage.myAgentActiveDetailDialog.aifClaimed")}{" "}
                <span
                  className="mono-num font-semibold"
                  style={{ color: C.text }}
                >
                  {aifClaimedDisplay}
                </span>
              </span>
              <span style={{ color: C.dim }}>
                {t("homePage.myAgentActiveDetailDialog.aifDaily")}{" "}
                <span
                  className="mono-num font-semibold"
                  style={{ color: C.text }}
                >
                  {dailyAifDisplay}
                </span>
              </span>
            </div>
          </div>

          {/* 购买信息 */}
          <div className="rounded-xl p-3.5 space-y-2.5" style={PANEL_STYLE}>
            {purchaseRows.map(item => (
              <div
                key={item.label}
                className="flex justify-between text-xs py-0.5 gap-3"
              >
                <span className="shrink-0" style={{ color: C.dim }}>
                  {item.label}
                </span>
                <span
                  className="mono-num font-bold text-right"
                  style={{ color: item.color ?? C.text }}
                >
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
